import type {
	SRT_ColumnDef,
	SRT_RowData,
} from '@/shared/components/master-table'
import type { FieldSchemaModel } from '@/shared/generated'
import type { TreeNodeType } from '@/shared/lib/tree'
import type { DataOption } from '../model/types'
import type { FieldSchemaProcessor } from './helpers'

import { useMemo } from 'react'
import { keyBy } from 'lodash/fp'

export function useColumnsFields<T extends SRT_RowData>({
	schema,
	getChoices,
	processor,
}: {
	schema: FieldSchemaModel[]
	getChoices: (
		schema: FieldSchemaModel[]
	) => Record<string, DataOption[] | TreeNodeType[]>
	processor: FieldSchemaProcessor<T>
}) {
	const choices = useMemo(() => getChoices(schema || []), [schema, getChoices])
	const isTree = (
		choices: DataOption[] | TreeNodeType[]
	): choices is TreeNodeType[] =>
		choices[0] && ('children' in choices[0] || 'parent' in choices[0])

	return {
		dataChoices: choices,
		columns: schema
			?.map(
				(field) =>
					({
						...field,
						getChoices: () =>
							choices?.[field.id]
								? isTree(choices?.[field.id])
									? choices?.[field.id]
									: keyBy('value', choices?.[field.id])
								: {},
					}) as FieldSchemaModel & {
						getChoices(): Record<string, DataOption | TreeNodeType>
					}
			)

			.map(processor) as SRT_ColumnDef<T, any>[],
	}
}
